"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ThemeContext = exports.ThemeProvider = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const React = __importStar(require("react"));
const classnames_1 = __importDefault(require("classnames"));
const index_js_1 = require("../utils/index.js");
require("@itwin/itwinui-css/css/global.css");
require("@itwin/itwinui-variables/index.css");
/**
 * This component provides global styles and applies theme to the entire tree
 * that it is wrapping around. The `theme` prop is optional and defaults to the
 * light theme.
 *
 * If you want to theme the entire app, you should use this component at the root. You can also
 * use this component to apply a different theme to only a part of the tree.
 *
 * By default, the topmost `ThemeProvider` in the tree will apply the recommended
 * `background-color`. You can override this behavior using `themeOptions.applyBackground`.
 *
 * @example
 * <ThemeProvider theme='os'>
 *  <App />
 * </ThemeProvider>
 *
 * @example
 * <ThemeProvider as='body'>
 *   <App />
 * </ThemeProvider>
 *
 * @example
 * <ThemeProvider theme='dark' themeOptions={{ applyBackground: false }}>
 *  <App />
 * </ThemeProvider>
 */
exports.ThemeProvider = React.forwardRef((props, ref) => {
    var _a;
    const { theme: themeProp, children, themeOptions, ...rest } = props;
    const rootRef = React.useRef(null);
    const mergedRefs = (0, index_js_1.useMergedRefs)(rootRef, ref);
    const hasChildren = React.Children.count(children) > 0;
    const parentContext = React.useContext(exports.ThemeContext);
    const theme = themeProp === 'inherit' ? (_a = parentContext === null || parentContext === void 0 ? void 0 : parentContext.theme) !== null && _a !== void 0 ? _a : 'light' : themeProp;
    const contextValue = React.useMemo(() => ({ theme, themeOptions, rootRef }), [theme, themeOptions]);
    // if no children, then fallback to this wrapper component which calls useTheme
    if (!hasChildren) {
        return (React.createElement(ThemeLogicWrapper, { theme: theme, themeOptions: themeOptions !== null && themeOptions !== void 0 ? themeOptions : parentContext === null || parentContext === void 0 ? void 0 : parentContext.themeOptions }));
    }
    // now that we know there are children, we can render the root and provide the context value
    return (React.createElement(Root, { theme: theme, isInheritingTheme: themeProp === 'inherit', themeOptions: themeOptions, ref: mergedRefs, ...rest },
        React.createElement(exports.ThemeContext.Provider, { value: contextValue }, children)));
});
exports.default = exports.ThemeProvider;
exports.ThemeContext = React.createContext(undefined);
const Root = React.forwardRef((props, forwardedRef) => {
    var _a, _b, _c;
    const { theme, children, themeOptions, as: Element = 'div', className, isInheritingTheme, ...rest } = props;
    const ref = React.useRef(null);
    const mergedRefs = (0, index_js_1.useMergedRefs)(ref, forwardedRef);
    const prefersDark = (0, index_js_1.useMediaQuery)('(prefers-color-scheme: dark)');
    const prefersHighContrast = (0, index_js_1.useMediaQuery)('(prefers-contrast: more)');
    const shouldApplyDark = theme === 'dark' || (theme === 'os' && prefersDark);
    const shouldApplyHC = (_a = themeOptions === null || themeOptions === void 0 ? void 0 : themeOptions.highContrast) !== null && _a !== void 0 ? _a : prefersHighContrast;
    const isThemeAlreadySet = (0, index_js_1.useIsThemeAlreadySet)((_b = ref.current) === null || _b === void 0 ? void 0 : _b.ownerDocument);
    const shouldApplyBackground = (_c = themeOptions === null || themeOptions === void 0 ? void 0 : themeOptions.applyBackground) !== null && _c !== void 0 ? _c : (isInheritingTheme ? false : !isThemeAlreadySet.current);
    return (React.createElement(Element, { className: (0, classnames_1.default)('iui-root', { 'iui-root-background': shouldApplyBackground }, className), "data-iui-theme": shouldApplyDark ? 'dark' : 'light', "data-iui-contrast": shouldApplyHC ? 'high' : 'default', ref: mergedRefs, ...rest }, children));
});
const ThemeLogicWrapper = (props) => {
    const { theme, themeOptions } = props;
    (0, index_js_1.useTheme)(theme, themeOptions);
    return React.createElement(React.Fragment, null);
};
